import { isEqual } from "es-toolkit";
import { atom } from "jotai";
import { atomFamily } from "jotai/utils";

import { Logger } from "@moment/logging";
import { ClientStoreV2, RebootWebTransport, type StoreV2 } from "@moment/reboot-differential";

import { $singletonRebootContext } from "./reboot-context.atom";
import { type DocumentRefProps, documentStoreV2Ref } from "./refs";

const logger = new Logger("document-store-v2-connection");

export interface DocumentStoreV2Connection {
	store: ClientStoreV2;
	storeRef: StoreV2.WeakReference;
}

/**
 * The canonical v2 Store connection for a document, as identified by workspace slug,
 * document ID, branch, and SHA.
 *
 * Returns null if the application-wide singleton Reboot `WebContext` is not available.
 *
 * This atom:
 * 1. Calls ensureCreated (idempotent) to handle docs shared before v2 existed
 * 2. Creates a RebootWebTransport + client Store
 * 3. Hydrates the store with current server state
 */
export const $documentStoreV2Connection = atomFamily((props: DocumentRefProps) => {
	const connectionAtom = atom(async (get): Promise<DocumentStoreV2Connection | null> => {
		// Guard: skip empty/invalid props (used as placeholder when collab is not active)
		if (!props.documentId || !props.workspaceSlug) {
			return null;
		}

		const context = get($singletonRebootContext);
		if (!context) {
			logger.warn("RebootContext not available", { ...props });
			return null;
		}

		try {
			const storeRef = documentStoreV2Ref(props);

			// Idempotent: ensure the v2 store exists (handles docs shared before v2 was added).
			// Response returns version + table schemas (no row data).
			const ensureResp = await storeRef.ensureCreated(context, {});

			// Build schema from the response table schemas.
			const schema: Record<string, { primaryKey: string }> = {};
			for (const [tableName, tableSchema] of Object.entries(ensureResp.tables)) {
				schema[tableName] = { primaryKey: tableSchema.primaryKey || "id" };
			}

			// Create the transport and client store, then hydrate via Snapshot RPC.
			const transport = new RebootWebTransport(storeRef, context);
			const store = new ClientStoreV2(schema, transport);
			await store.hydrate();

			logger.info("Created document store v2 connection", { ...props });

			return { store, storeRef };
		} catch (err) {
			logger.error("Failed to create document store v2 connection", { ...props, err });
			return null;
		}
	});

	connectionAtom.debugLabel = `documentStoreV2Connection/${props.workspaceSlug}/${props.documentId}/${props.branch}/${props.sha}`;
	return connectionAtom;
}, isEqual);
